import {
  formatEther,
  formatUnits,
  parseEther,
  parseUnits,
  readContract,
  writeContractAndWait,
} from "../viem";
import config from "../../config/config";
import MafiaExchangeAbi from "../../abis/MafiaExchange.json";
import MafiaDepositAbi from "../../abis/MafiaDeposit.json";
import VaultSplitterAbi from "../../abis/VaultSplitter.json";
import { OTCRequestItem } from "../../types/Contract/Exchange/OTCRequestItem";
import { approveGameCash, getGameBankAllowance } from "./MafiaGameBank";
import { SwapToken } from "../../types/Contract/SwapToken";

export const getExchangeTotalFee = async () => {
  const balance = await readContract<string>({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "totalFees",
  });

  return formatUnits(balance, 18);
};

export const convertItems = async (itemIds: number[], account: string) => {
  const data = await writeContractAndWait({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "convertItem",
    args: [itemIds],
    account,
  });

  return data;
};

export const createOTCTrade = async (
  itemIds: number[],
  requestItems: OTCRequestItem[],
  account: string
) => {
  const data = await writeContractAndWait({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "createOTCOffer",
    args: [itemIds, requestItems],
    account,
  });

  return data;
};

export const acceptOTCTrade = async (
  offerId: number,
  itemIds: number[],
  account: string
) => {
  const data = await writeContractAndWait({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "acceptOTCOffer",
    args: [offerId, itemIds],
    account,
  });

  return data;
};

export const cancelOTCTrade = async (offerId: number, account: string) => {
  const data = await writeContractAndWait({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "cancelOTCOffer",
    args: [offerId],
    account,
  });

  return data;
};

export const cancelExpiredOTCOffers = async (
  offerIds: number[],
  account: string
) => {
  const data = await writeContractAndWait({
    address: config.exchangeAddress,
    abi: MafiaExchangeAbi,
    functionName: "cancelExpiredOTCOffers",
    args: [offerIds],
    account,
  });

  return data;
};

export const approveInGameCashToDepositContract = async (
  account: string,
  amount: number
) => {
  const allowance = await getGameBankAllowance(account, config.depositAddress);

  if (allowance < amount) {
    await approveGameCash(amount, config.depositAddress, account);
  }
};

export const addNewLiquidity = async (
  account: string,
  cash: number,
  cashPerMafia: number
) => {
  await writeContractAndWait({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "addLiquidity",
    args: [parseEther(cash.toString()), parseEther(cashPerMafia.toString())],
    account,
  });
};

export const removeLiquidity = async (id: number, account: string) => {
  await writeContractAndWait({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "removeLiquidity",
    args: [id],
    account,
  });
};

export const withdrawMAFIA = async (id: number, account: string) => {
  await writeContractAndWait({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "withdrawMafia",
    args: [id],
    account,
  });
};

export const swapCash = async (
  tokenId: number,
  account: string,
  amount: number,
  tokenInfo: SwapToken
) => {
  if (tokenId === 0) {
    const nativeAmount = parseEther(amount.toFixed(18));
    const sendAmount = parseEther((amount * 1.001).toFixed(18));

    await writeContractAndWait({
      address: config.depositAddress,
      abi: MafiaDepositAbi,
      functionName: "swapCash",
      args: [tokenId, nativeAmount],
      account,
      value: sendAmount,
    });
  } else {
    const amountWei = parseUnits(
      amount.toFixed(tokenInfo.decimal),
      tokenInfo.decimal
    );
    await writeContractAndWait({
      address: config.depositAddress,
      abi: MafiaDepositAbi,
      functionName: "swapCash",
      args: [tokenId, amountWei],
      account,
    });
  }
};

export const getSwapTokens = async () => {
  const swapTokenInfo: [SwapToken[], number[]] = await readContract({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "getSwapTokens",
  });

  return swapTokenInfo;
};

export const getCashWithEstimateSwap = async (amount: number) => {
  const cash = await readContract<string>({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "estimateSwap",
    args: [parseEther(amount.toString())],
  });
  return Number(formatEther(cash));
};

export const getAvailablePrices = async () => {
  const availablePrices = await readContract<string[]>({
    address: config.depositAddress,
    abi: MafiaDepositAbi,
    functionName: "getAvailablePrices",
  });

  return availablePrices.map((item) => Number(formatEther(item)));
};

export const executeVaultSplitterSwap = async (account: string) => {
  await writeContractAndWait({
    address: config.vaultSplitterAddress,
    abi: VaultSplitterAbi,
    functionName: "swapCash",
    account,
    gas: 2000000,
  });
};

export const sendToTeamWallet = async (account: string) => {
  await writeContractAndWait({
    address: config.vaultSplitterAddress,
    abi: VaultSplitterAbi,
    functionName: "sendToTeamWallet",
    account,
    gas: 2000000,
  });
};
